import { getSupabaseClient } from './supabase';

// Shared helper to download an image from a URL to the user's device.
// Tries multiple strategies to force an actual file download (instead of
// opening the image in a new tab), falling back gracefully only as a last resort.

function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'image';
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Derive a proper file extension from the blob's MIME type so the saved file
// opens correctly (e.g. a .webp blob saved as .png would be corrupt).
function extensionForBlob(blob: Blob): string {
  const type = (blob.type || '').toLowerCase();
  if (type.includes('webp')) return 'webp';
  if (type.includes('png')) return 'png';
  if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
  if (type.includes('gif')) return 'gif';
  if (type.includes('svg')) return 'svg';
  return 'png';
}

function finalizeName(filename: string, blob: Blob): string {
  const base = filename.replace(/\.[^./]+$/, '');
  return `${base}.${extensionForBlob(blob)}`;
}

async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const finalName = finalizeName(filename, blob);
  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerDownload(objectUrl, finalName);
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 3000);
  }
}

// Parse a Supabase Storage URL (signed or public) into { bucket, path }.
// Signed URL:   .../storage/v1/object/sign/<bucket>/<path>?token=...
// Public URL:   .../storage/v1/object/public/<bucket>/<path>
function parseSupabaseStorageUrl(url: string): { bucket: string; path: string } | null {
  const clean = url.split('?')[0];
  const marker = '/storage/v1/object/';
  const idx = clean.indexOf(marker);
  if (idx === -1) return null;
  const segments = clean.slice(idx + marker.length).split('/');
  // segments = [ 'sign' | 'public' | 'authenticated', bucket, ...path ]
  if (segments.length < 3) return null;
  try {
    const bucket = decodeURIComponent(segments[1]);
    const path = segments.slice(2).map((s) => decodeURIComponent(s)).join('/');
    if (!bucket || !path) return null;
    return { bucket, path };
  } catch {
    return null;
  }
}

// Download via the Supabase Storage client. This works for private buckets too
// (the signed-URL CORS problem does not apply), because the SDK sends the
// authenticated session and Supabase Storage returns proper CORS headers.
async function trySupabaseStorageDownload(url: string): Promise<Blob | null> {
  const parsed = parseSupabaseStorageUrl(url);
  if (!parsed) return null;
  const client = getSupabaseClient();
  if (!client) return null;
  try {
    const { data, error } = await client.storage.from(parsed.bucket).download(parsed.path);
    if (error) {
      console.error('[downloadImage] storage.download error:', error?.message || error);
      return null;
    }
    if (!data) return null;
    return data;
  } catch (err) {
    console.error('[downloadImage] storage.download exception:', err);
    return null;
  }
}

// Draw the (already cross-origin loadable) image onto a canvas and export as PNG.
function canvasToBlob(url: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (blob: Blob | null) => {
      if (!settled) {
        settled = true;
        resolve(blob);
      }
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => finish(blob), 'image/png');
      } catch (err) {
        console.error('[downloadImage] canvas export error:', err);
        finish(null);
      }
    };
    img.onerror = () => {
      console.error('[downloadImage] canvas image load error (CORS?)');
      finish(null);
    };
    img.src = url;
  });
}

function fallbackOpen(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function downloadImage(url: string, filename?: string): Promise<void> {
  const name = filename || 'image';

  if (!url) return;

  // Strategy 1: Supabase Storage download (handles private buckets + signed URLs).
  try {
    const blob = await trySupabaseStorageDownload(url);
    if (blob) {
      await saveBlob(blob, name);
      return;
    }
  } catch {
    // ignore, try next strategy
  }

  // Strategy 2: fetch as blob (works for public URLs with CORS).
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (res.ok) {
      const blob = await res.blob();
      if (blob && blob.size > 0) {
        await saveBlob(blob, name);
        return;
      }
    } else {
      console.error('[downloadImage] fetch status:', res.status);
    }
  } catch (err) {
    console.error('[downloadImage] fetch error:', err);
  }

  // Strategy 3: canvas export (also requires CORS, works for displayed images).
  try {
    const blob = await canvasToBlob(url);
    if (blob) {
      await saveBlob(blob, name);
      return;
    }
  } catch {
    // ignore, try next strategy
  }

  // Strategy 4: last resort — open in a new tab (cross-origin without CORS).
  console.error('[downloadImage] all strategies failed, falling back to new tab');
  fallbackOpen(url);
}